import React from 'react';
import {
  Activity,
  Apple,
  BookHeart,
  Building2,
  Contact,
  Dumbbell,
  Eye,
  Frame,
  HeartPulse,
  LayoutDashboard,
  Linkedin,
  MessageCircle,
  Settings as SettingsIcon,
  Share2,
  Sparkles,
  Stethoscope,
  Sunrise,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { BossLogo } from './BossLogo';
import { NavTab } from './NavTab';
import { VoiceControl } from '../VoiceControl';
import { useProduct } from '../../context/ProductContext';

function getUserRole(): string {
  try {
    const raw = localStorage.getItem('boss_user');
    if (!raw) return 'user';
    return JSON.parse(raw).role ?? 'user';
  } catch {
    return 'user';
  }
}

interface NavRailProps {
  collapsed: boolean;
  onCollapseToggle?: () => void;
  onNavClick?: () => void;
  immersive?: boolean;
}

export function NavRail({ collapsed, onCollapseToggle, onNavClick, immersive = false }: NavRailProps) {
  const isAdmin = ['admin', 'owner'].includes(getUserRole());
  const railBg = immersive ? 'rgba(5, 8, 15, 0.34)' : 'var(--v-nav-bg)';
  // Health and the other personal modules belong to HOS. Keeping them out of the
  // BOS rail is the point of the split — the business surface should not carry
  // the home surface's pages.
  const { config, activeProduct, moduleEnabled } = useProduct();
  const onHos = activeProduct === 'hos';
  // HOS partnered with BOS defers social/WhatsApp to the business surface;
  // standalone HOS owns them outright.
  const standaloneHos = config.edition === 'hos';
  const paName = config.paName || 'Assistant';

  const sectionLabel = (label: string) =>
    !collapsed ? (
      <div className="vs-mono px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.22em] text-text-muted">
        {label}
      </div>
    ) : (
      <div className="h-px bg-border mx-2 my-2" aria-hidden />
    );

  return (
    <nav
      className={[
        'boss-nav-surface relative flex flex-col h-full border-r border-border',
        'transition-[width] duration-200',
        immersive ? 'backdrop-blur-xl shadow-2xl' : '',
        collapsed ? 'w-[56px]' : 'w-[212px]',
      ].join(' ')}
      style={{ background: railBg }}
      aria-label="Main navigation"
    >
      <div className="vs-aurora opacity-25 pointer-events-none" aria-hidden />

      <div className="relative flex-shrink-0 border-b border-border">
        <BossLogo collapsed={collapsed} onCollapseToggle={onCollapseToggle} />
      </div>

      <div className="relative flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {onHos ? (
          <>
            <NavTab to="/hos" end icon={LayoutDashboard} label="Personal" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/hos/assistant" icon={Sparkles} label={paName} collapsed={collapsed} onNavClick={onNavClick} />

            {moduleEnabled('health') && (
              <>
                {sectionLabel('Wellbeing')}
                <NavTab to="/hos/health" end icon={HeartPulse} label="Health" collapsed={collapsed} onNavClick={onNavClick} />
                <NavTab to="/hos/health/holo" icon={Activity} label="Vitals" collapsed={collapsed} onNavClick={onNavClick} />
                <NavTab to="/hos/health/journal" icon={BookHeart} label="Journal" collapsed={collapsed} onNavClick={onNavClick} />
                <NavTab to="/hos/health/records" icon={Stethoscope} label="Records" collapsed={collapsed} onNavClick={onNavClick} />
              </>
            )}
            {moduleEnabled('fitness') && (
              <NavTab to="/hos/fitness" icon={Dumbbell} label="Fitness" collapsed={collapsed} onNavClick={onNavClick} />
            )}
            {moduleEnabled('nutrition') && (
              <NavTab to="/hos/nutrition" icon={Apple} label="Nutrition" collapsed={collapsed} onNavClick={onNavClick} />
            )}
            {moduleEnabled('spiritual') && (
              <NavTab to="/hos/spiritual" icon={Sunrise} label="Spiritual" collapsed={collapsed} onNavClick={onNavClick} />
            )}

            {(moduleEnabled('finances') || moduleEnabled('address-book') || standaloneHos) && sectionLabel('Life')}
            {moduleEnabled('finances') && (
              <NavTab to="/hos/finances" icon={Wallet} label="Finances" collapsed={collapsed} onNavClick={onNavClick} />
            )}
            {moduleEnabled('address-book') && (
              <NavTab to="/hos/address-book" icon={Contact} label="Address Book" collapsed={collapsed} onNavClick={onNavClick} />
            )}
            {/* Social and WhatsApp are HOS surfaces only when HOS stands alone.
                Partnered with BOS they are managed from the business side. */}
            {standaloneHos && moduleEnabled('social') && (
              <NavTab to="/hos/social" icon={Share2} label="Social" collapsed={collapsed} onNavClick={onNavClick} />
            )}
            {standaloneHos && (moduleEnabled('social') || moduleEnabled('address-book')) && (
              <NavTab to="/hos/whatsapp" icon={MessageCircle} label="WhatsApp" collapsed={collapsed} onNavClick={onNavClick} />
            )}
          </>
        ) : (
          <>
            <NavTab to="/" end icon={LayoutDashboard} label="Dashboard" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/office" icon={Building2} label="Office" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/board" icon={Eye} label="Advisory Board" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/rascals" icon={Users} label="Client Managers" collapsed={collapsed} onNavClick={onNavClick} />

            {sectionLabel('Surfaces')}

            <NavTab to="/canvas" icon={Frame} label="Canvas" variant="miro" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/crm" icon={UserCheck} label="CRM" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/linkedin" icon={Linkedin} label="LinkedIn" collapsed={collapsed} onNavClick={onNavClick} />
            <NavTab to="/whatsapp" icon={MessageCircle} label="WhatsApp" collapsed={collapsed} onNavClick={onNavClick} />
          </>
        )}

        {/* Settings is install-wide, so it stays reachable from either surface. */}
        {isAdmin && (
          <>
            {sectionLabel('Control')}
            <NavTab to="/settings" icon={SettingsIcon} label="Settings" collapsed={collapsed} onNavClick={onNavClick} />
          </>
        )}
      </div>

      <div className="relative flex-shrink-0 border-t border-border">
        <div className={collapsed ? 'flex flex-col items-center py-2' : 'flex items-center px-3 py-2'}>
          <VoiceControl />
        </div>
      </div>
    </nav>
  );
}
